import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Plus, Edit2, Trash2, Save, X, Upload, Image as ImageIcon } from 'lucide-react';

interface Bank {
  bank_id: string;
  bank_name: string;
}

interface Campaign {
  id: string;
  bank_id: string;
  bank_name: string;
  title: string;
  description: string;
  campaign_image: string;
  is_hero: boolean;
  offer_details: string[];
  end_date: string;
  terms: string[];
  display_order: number;
  is_active: boolean;
}

interface Credit {
  id: string;
  bank_id: string;
  bank_name: string;
  product_name: string;
  description: string;
  icon_type: string;
  interest_rate: string;
  max_amount: string;
  features: string[];
  button_url: string;
  display_order: number;
  is_active: boolean;
}

interface Card {
  id: string;
  bank_id: string;
  bank_name: string;
  card_name: string;
  description: string;
  icon_type: string;
  annual_fee: string;
  cashback: string;
  features: string[];
  button_url: string;
  display_order: number;
  is_active: boolean;
}

const BankDetailManagement = () => {
  const [banks, setBanks] = useState<Bank[]>([]);
  const [selectedBank, setSelectedBank] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'campaigns' | 'credits' | 'cards'>('campaigns');

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [credits, setCredits] = useState<Credit[]>([]);
  const [cards, setCards] = useState<Card[]>([]);

  const [editingItem, setEditingItem] = useState<any>(null);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    fetchBanks();
  }, []);

  useEffect(() => {
    if (selectedBank) {
      fetchBankData();
    }
  }, [selectedBank, activeTab]);

  const fetchBanks = async () => {
    const { data, error } = await supabase
      .from('bank_offers')
      .select('bank_id, bank_name')
      .eq('is_active', true)
      .order('bank_name');

    if (data && !error) {
      setBanks(data);
      if (data.length > 0) {
        setSelectedBank(data[0].bank_id);
      }
    }
  };

  const fetchBankData = async () => {
    if (activeTab === 'campaigns') {
      const { data } = await supabase
        .from('bank_campaigns')
        .select('*')
        .eq('bank_id', selectedBank)
        .order('display_order');
      if (data) setCampaigns(data);
    } else if (activeTab === 'credits') {
      const { data } = await supabase
        .from('bank_credits')
        .select('*')
        .eq('bank_id', selectedBank)
        .order('display_order');
      if (data) setCredits(data);
    } else if (activeTab === 'cards') {
      const { data } = await supabase
        .from('bank_cards')
        .select('*')
        .eq('bank_id', selectedBank)
        .order('display_order');
      if (data) setCards(data);
    }
  };

  const handleSaveCampaign = async (campaign: Campaign) => {
    const bankName = banks.find(b => b.bank_id === selectedBank)?.bank_name || '';

    if (campaign.id) {
      await supabase
        .from('bank_campaigns')
        .update({
          ...campaign,
          bank_name: bankName,
          updated_at: new Date().toISOString()
        })
        .eq('id', campaign.id);
    } else {
      await supabase
        .from('bank_campaigns')
        .insert({
          ...campaign,
          bank_id: selectedBank,
          bank_name: bankName
        });
    }

    setEditingItem(null);
    setIsAdding(false);
    fetchBankData();
  };

  const handleSaveCredit = async (credit: Credit) => {
    const bankName = banks.find(b => b.bank_id === selectedBank)?.bank_name || '';

    if (credit.id) {
      await supabase
        .from('bank_credits')
        .update({
          ...credit,
          bank_name: bankName,
          updated_at: new Date().toISOString()
        })
        .eq('id', credit.id);
    } else {
      await supabase
        .from('bank_credits')
        .insert({
          ...credit,
          bank_id: selectedBank,
          bank_name: bankName
        });
    }

    setEditingItem(null);
    setIsAdding(false);
    fetchBankData();
  };

  const handleSaveCard = async (card: Card) => {
    const bankName = banks.find(b => b.bank_id === selectedBank)?.bank_name || '';

    if (card.id) {
      await supabase
        .from('bank_cards')
        .update({
          ...card,
          bank_name: bankName,
          updated_at: new Date().toISOString()
        })
        .eq('id', card.id);
    } else {
      await supabase
        .from('bank_cards')
        .insert({
          ...card,
          bank_id: selectedBank,
          bank_name: bankName
        });
    }

    setEditingItem(null);
    setIsAdding(false);
    fetchBankData();
  };

  const handleDelete = async (id: string, table: string) => {
    if (confirm('Bu öğeyi silmek istediğinizden emin misiniz?')) {
      await supabase
        .from(table)
        .delete()
        .eq('id', id);
      fetchBankData();
    }
  };

  const startAdding = () => {
    const bankName = banks.find(b => b.bank_id === selectedBank)?.bank_name || '';

    if (activeTab === 'campaigns') {
      setEditingItem({
        bank_id: selectedBank,
        bank_name: bankName,
        title: '',
        description: '',
        campaign_image: '',
        is_hero: false,
        offer_details: [],
        end_date: '',
        terms: [],
        display_order: campaigns.length,
        is_active: true
      });
    } else if (activeTab === 'credits') {
      setEditingItem({
        bank_id: selectedBank,
        bank_name: bankName,
        product_name: '',
        description: '',
        icon_type: 'wallet',
        interest_rate: '',
        max_amount: '',
        features: [],
        button_url: '',
        display_order: credits.length,
        is_active: true
      });
    } else if (activeTab === 'cards') {
      setEditingItem({
        bank_id: selectedBank,
        bank_name: bankName,
        card_name: '',
        description: '',
        icon_type: 'credit-card',
        annual_fee: '',
        cashback: '',
        features: [],
        button_url: '',
        display_order: cards.length,
        is_active: true
      });
    }
    setIsAdding(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-slate-800">Banka Detay Sayfası Yönetimi</h2>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Banka Seçin
          </label>
          <select
            value={selectedBank}
            onChange={(e) => setSelectedBank(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {banks.map((bank) => (
              <option key={bank.bank_id} value={bank.bank_id}>
                {bank.bank_name}
              </option>
            ))}
          </select>
        </div>

        <div className="border-b border-slate-200 mb-6">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('campaigns')}
              className={`pb-3 px-4 font-medium transition-colors ${
                activeTab === 'campaigns'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Kampanyalar
            </button>
            <button
              onClick={() => setActiveTab('credits')}
              className={`pb-3 px-4 font-medium transition-colors ${
                activeTab === 'credits'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Krediler
            </button>
            <button
              onClick={() => setActiveTab('cards')}
              className={`pb-3 px-4 font-medium transition-colors ${
                activeTab === 'cards'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Kredi Kartları
            </button>
          </div>
        </div>

        <div className="mb-4">
          <button
            onClick={startAdding}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={20} />
            Yeni Ekle
          </button>
        </div>

        {activeTab === 'campaigns' && (
          <CampaignList
            campaigns={campaigns}
            editingItem={editingItem}
            isAdding={isAdding}
            onEdit={setEditingItem}
            onSave={handleSaveCampaign}
            onDelete={(id) => handleDelete(id, 'bank_campaigns')}
            onCancel={() => {
              setEditingItem(null);
              setIsAdding(false);
            }}
          />
        )}

        {activeTab === 'credits' && (
          <CreditList
            credits={credits}
            editingItem={editingItem}
            isAdding={isAdding}
            onEdit={setEditingItem}
            onSave={handleSaveCredit}
            onDelete={(id) => handleDelete(id, 'bank_credits')}
            onCancel={() => {
              setEditingItem(null);
              setIsAdding(false);
            }}
          />
        )}

        {activeTab === 'cards' && (
          <CardList
            cards={cards}
            editingItem={editingItem}
            isAdding={isAdding}
            onEdit={setEditingItem}
            onSave={handleSaveCard}
            onDelete={(id) => handleDelete(id, 'bank_cards')}
            onCancel={() => {
              setEditingItem(null);
              setIsAdding(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

const CampaignList = ({ campaigns, editingItem, isAdding, onEdit, onSave, onDelete, onCancel }: any) => {
  return (
    <div className="space-y-4">
      {isAdding && <CampaignEditor item={editingItem} onSave={onSave} onCancel={onCancel} />}

      {campaigns.map((campaign: Campaign) => (
        <div key={campaign.id}>
          {editingItem?.id === campaign.id ? (
            <CampaignEditor item={editingItem} onSave={onSave} onCancel={onCancel} />
          ) : (
            <div className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-bold text-lg text-slate-800">{campaign.title}</h3>
                    {campaign.is_hero && (
                      <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">
                        Ana Kampanya
                      </span>
                    )}
                    {!campaign.is_active && (
                      <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
                        Pasif
                      </span>
                    )}
                  </div>
                  <p className="text-slate-600 mb-2">{campaign.description}</p>
                  {campaign.campaign_image && (
                    <img
                      src={campaign.campaign_image}
                      alt={campaign.title}
                      className="w-32 h-20 object-cover rounded mt-2"
                    />
                  )}
                  {campaign.end_date && (
                    <p className="text-sm text-slate-500 mt-2">Bitiş: {campaign.end_date}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => onEdit(campaign)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => onDelete(campaign.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const CampaignEditor = ({ item, onSave, onCancel }: any) => {
  const [formData, setFormData] = useState(item);

  const handleSave = () => {
    onSave(formData);
  };

  return (
    <div className="border-2 border-blue-300 rounded-lg p-4 bg-blue-50">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Başlık</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Açıklama</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            rows={3}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Kampanya Görseli URL
          </label>
          <input
            type="text"
            value={formData.campaign_image}
            onChange={(e) => setFormData({ ...formData, campaign_image: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            placeholder="https://example.com/image.jpg"
          />
          {formData.campaign_image && (
            <img
              src={formData.campaign_image}
              alt="Preview"
              className="w-48 h-32 object-cover rounded mt-2"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Bitiş Tarihi</label>
          <input
            type="text"
            value={formData.end_date}
            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            placeholder="31 Aralık 2025"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Kampanya Detayları (Her satır bir madde)
          </label>
          <textarea
            value={formData.offer_details?.join('\n') || ''}
            onChange={(e) => setFormData({ ...formData, offer_details: e.target.value.split('\n').filter(Boolean) })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            rows={4}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Şartlar ve Koşullar (Her satır bir madde)
          </label>
          <textarea
            value={formData.terms?.join('\n') || ''}
            onChange={(e) => setFormData({ ...formData, terms: e.target.value.split('\n').filter(Boolean) })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            rows={4}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Sıra</label>
            <input
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.is_hero}
              onChange={(e) => setFormData({ ...formData, is_hero: e.target.checked })}
              className="w-4 h-4"
            />
            <label className="text-sm font-medium text-slate-700">Ana Kampanya</label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4"
            />
            <label className="text-sm font-medium text-slate-700">Aktif</label>
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <X size={18} />
            İptal
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save size={18} />
            Kaydet
          </button>
        </div>
      </div>
    </div>
  );
};

const CreditList = ({ credits, editingItem, isAdding, onEdit, onSave, onDelete, onCancel }: any) => {
  return (
    <div className="space-y-4">
      {isAdding && <CreditEditor item={editingItem} onSave={onSave} onCancel={onCancel} />}

      {credits.map((credit: Credit) => (
        <div key={credit.id}>
          {editingItem?.id === credit.id ? (
            <CreditEditor item={editingItem} onSave={onSave} onCancel={onCancel} />
          ) : (
            <div className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-bold text-lg text-slate-800">{credit.product_name}</h3>
                    {!credit.is_active && (
                      <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
                        Pasif
                      </span>
                    )}
                  </div>
                  <p className="text-slate-600 mb-2">{credit.description}</p>
                  {credit.interest_rate && (
                    <p className="text-sm text-slate-500">Faiz: {credit.interest_rate}</p>
                  )}
                  {credit.max_amount && (
                    <p className="text-sm text-slate-500">Maksimum Tutar: {credit.max_amount}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => onEdit(credit)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => onDelete(credit.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const CreditEditor = ({ item, onSave, onCancel }: any) => {
  const [formData, setFormData] = useState(item);

  const handleSave = () => {
    onSave(formData);
  };

  return (
    <div className="border-2 border-blue-300 rounded-lg p-4 bg-blue-50">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Ürün Adı</label>
          <input
            type="text"
            value={formData.product_name}
            onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Açıklama</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            rows={3}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Faiz Oranı</label>
            <input
              type="text"
              value={formData.interest_rate}
              onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              placeholder="%2.50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Maksimum Tutar</label>
            <input
              type="text"
              value={formData.max_amount}
              onChange={(e) => setFormData({ ...formData, max_amount: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              placeholder="100.000 TL"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Başvuru URL</label>
          <input
            type="text"
            value={formData.button_url}
            onChange={(e) => setFormData({ ...formData, button_url: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            placeholder="https://example.com/apply"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Özellikler (Her satır bir madde)
          </label>
          <textarea
            value={formData.features?.join('\n') || ''}
            onChange={(e) => setFormData({ ...formData, features: e.target.value.split('\n').filter(Boolean) })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            rows={4}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Sıra</label>
            <input
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4"
            />
            <label className="text-sm font-medium text-slate-700">Aktif</label>
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <X size={18} />
            İptal
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save size={18} />
            Kaydet
          </button>
        </div>
      </div>
    </div>
  );
};

const CardList = ({ cards, editingItem, isAdding, onEdit, onSave, onDelete, onCancel }: any) => {
  return (
    <div className="space-y-4">
      {isAdding && <CardEditor item={editingItem} onSave={onSave} onCancel={onCancel} />}

      {cards.map((card: Card) => (
        <div key={card.id}>
          {editingItem?.id === card.id ? (
            <CardEditor item={editingItem} onSave={onSave} onCancel={onCancel} />
          ) : (
            <div className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-bold text-lg text-slate-800">{card.card_name}</h3>
                    {!card.is_active && (
                      <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
                        Pasif
                      </span>
                    )}
                  </div>
                  <p className="text-slate-600 mb-2">{card.description}</p>
                  {card.annual_fee && (
                    <p className="text-sm text-slate-500">Yıllık Ücret: {card.annual_fee}</p>
                  )}
                  {card.cashback && (
                    <p className="text-sm text-slate-500">Cashback: {card.cashback}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => onEdit(card)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => onDelete(card.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const CardEditor = ({ item, onSave, onCancel }: any) => {
  const [formData, setFormData] = useState(item);

  const handleSave = () => {
    onSave(formData);
  };

  return (
    <div className="border-2 border-blue-300 rounded-lg p-4 bg-blue-50">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Kart Adı</label>
          <input
            type="text"
            value={formData.card_name}
            onChange={(e) => setFormData({ ...formData, card_name: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Açıklama</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            rows={3}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Yıllık Ücret</label>
            <input
              type="text"
              value={formData.annual_fee}
              onChange={(e) => setFormData({ ...formData, annual_fee: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              placeholder="Ücretsiz veya 500 TL"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Cashback</label>
            <input
              type="text"
              value={formData.cashback}
              onChange={(e) => setFormData({ ...formData, cashback: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              placeholder="%5 Bonus"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Başvuru URL</label>
          <input
            type="text"
            value={formData.button_url}
            onChange={(e) => setFormData({ ...formData, button_url: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            placeholder="https://example.com/apply"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Özellikler (Her satır bir madde)
          </label>
          <textarea
            value={formData.features?.join('\n') || ''}
            onChange={(e) => setFormData({ ...formData, features: e.target.value.split('\n').filter(Boolean) })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            rows={4}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Sıra</label>
            <input
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4"
            />
            <label className="text-sm font-medium text-slate-700">Aktif</label>
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <X size={18} />
            İptal
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Save size={18} />
            Kaydet
          </button>
        </div>
      </div>
    </div>
  );
};

export default BankDetailManagement;
